/**
 * Source unique de vérité pour la segmentation client ATABLE (côté web).
 *
 * Règles d'or :
 * - Un client appartient à UN SEUL segment à la fois, ou à aucun (`null`).
 * - Le segment est calculé CHAQUE NUIT par la Cloud Function
 *   `recomputeCustomerSegments` (functions/src/segments) et stocké dans
 *   `customerSegments/{restaurantId}_{userId}`, lisible par le restaurateur seul.
 *   Le navigateur ne recalcule jamais : il lit.
 * - Les seuils sont paramétrables par restaurant dans `segmentRules/{restaurantId}`.
 *   Les défauts ci-dessous sont le miroir de functions/src/segments/types.ts —
 *   garder les deux alignés.
 *
 * Segments :
 * - **Nouveau**     : 1ère visite dans la fenêtre, 1 à 5 visites.
 * - **Habitué**     : ≥ 6 visites sur la fenêtre.
 * - **VIP**         : ≥ 300 € dépensés ou ≥ 10 visites sur la fenêtre.
 * - **Gros panier** : panier moyen ≥ 45 € (min. 2 visites) sur la fenêtre.
 * - **À risque**    : aucune visite depuis 45 jours, alors qu'il était déjà venu.
 *
 * Priorité si plusieurs règles sont remplies : À risque, VIP, Gros panier, Habitué, Nouveau.
 */

import type { CustomerSegmentKey, SegmentRules } from './types';

export type CustomerSegment = CustomerSegmentKey;

export const ALL_SEGMENTS: CustomerSegment[] = ['nouveau', 'habitue', 'vip', 'gros_panier', 'a_risque'];
/** Alias historique utilisé par le service et les campagnes. */
export const SEGMENT_KEYS = ALL_SEGMENTS;
/** Cartes affichées en haut de Base clients : les cinq segments. */
export const PRIMARY_SEGMENT_CARDS: CustomerSegment[] = ALL_SEGMENTS;

export const DEFAULT_SEGMENT_RULES: SegmentRules = {
    windowDays: 30,
    nouveau: { enabled: true, minVisits: 1, maxVisits: 5 },
    habitue: { enabled: true, minVisits: 6 },
    vip: { enabled: true, minSpend: 300, minVisits: 10 },
    grosPanier: { enabled: true, minAverageTicket: 45, minVisits: 2 },
    aRisque: { enabled: true, inactiveDays: 45 },
};

export const SEGMENT_LABELS: Record<CustomerSegment, string> = {
    nouveau: 'Nouveau',
    habitue: 'Habitué',
    vip: 'VIP',
    gros_panier: 'Gros panier',
    a_risque: 'À risque',
};

export const NO_SEGMENT_LABEL = 'Sans segment';

/** Descriptions courtes (défauts). Préférer `describeSegment` quand les règles du resto sont connues. */
export const SEGMENT_DESCRIPTIONS: Record<CustomerSegment, string> = {
    nouveau: 'Première visite récente.',
    habitue: 'Vient régulièrement.',
    vip: 'Dépense ou vient beaucoup.',
    gros_panier: 'Panier moyen élevé.',
    a_risque: 'Pas revenu depuis longtemps.',
};

/** Couleurs Tailwind pour pastilles et cartes (mode clair ; le mode sombre est adapté côté composant). */
export const SEGMENT_PALETTE: Record<CustomerSegment, { text: string; bg: string; ring: string }> = {
    nouveau:     { text: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
    habitue:     { text: 'text-sky-700',     bg: 'bg-sky-50',     ring: 'ring-sky-200' },
    vip:         { text: 'text-purple-700',  bg: 'bg-purple-50',  ring: 'ring-purple-200' },
    gros_panier: { text: 'text-amber-700',   bg: 'bg-amber-50',   ring: 'ring-amber-200' },
    a_risque:    { text: 'text-rose-700',    bg: 'bg-rose-50',    ring: 'ring-rose-200' },
};

export const NO_SEGMENT_PALETTE = { text: 'text-gray-600', bg: 'bg-gray-100', ring: 'ring-gray-200' };

/** Vue « meta » (clé + libellé + classes) pratique pour les composants qui itèrent. */
export interface SegmentMeta {
    key: CustomerSegment;
    label: string;
    color: string;
    bgColor: string;
}
export const SEGMENT_META: Record<CustomerSegment, SegmentMeta> = Object.fromEntries(
    ALL_SEGMENTS.map(k => [k, { key: k, label: SEGMENT_LABELS[k], color: SEGMENT_PALETTE[k].text, bgColor: SEGMENT_PALETTE[k].bg }]),
) as Record<CustomerSegment, SegmentMeta>;

export function isSegmentKey(value: unknown): value is CustomerSegment {
    return typeof value === 'string' && (ALL_SEGMENTS as string[]).includes(value);
}

export function segmentLabel(key: CustomerSegment | null | undefined): string {
    return key && SEGMENT_LABELS[key] ? SEGMENT_LABELS[key] : NO_SEGMENT_LABEL;
}

/** Libellé d'un ciblage de campagne : clé de segment, 'all', ou ancien libellé libre. */
export function targetSegmentLabel(target: string | undefined | null): string {
    if (!target || target === 'all') return 'Tous les membres';
    if (target === 'regulars') return SEGMENT_LABELS.habitue;
    return isSegmentKey(target) ? SEGMENT_LABELS[target] : target;
}

/** Phrase décrivant la règle d'un segment à partir des seuils du restaurant. */
export function describeSegment(key: CustomerSegment, r: SegmentRules): string {
    const w = `${r.windowDays} j`;
    switch (key) {
        case 'nouveau':
            return `1ère visite il y a moins de ${w}, ${r.nouveau.minVisits} à ${r.nouveau.maxVisits} visites.`;
        case 'habitue':
            return `${r.habitue.minVisits} visites ou plus sur ${w}.`;
        case 'vip':
            return `${r.vip.minSpend} € dépensés ou ${r.vip.minVisits} visites sur ${w}.`;
        case 'gros_panier':
            return `Panier moyen ≥ ${r.grosPanier.minAverageTicket} € (min. ${r.grosPanier.minVisits} visites) sur ${w}.`;
        case 'a_risque':
            return `Aucune visite depuis ${r.aRisque.inactiveDays} jours.`;
    }
}

/** Fusionne des règles partielles avec les défauts, sans valider (la Cloud Function le fait). */
export function mergeRules(input: Partial<SegmentRules> | null | undefined): SegmentRules {
    const D = DEFAULT_SEGMENT_RULES;
    const r = input ?? {};
    return {
        windowDays: r.windowDays ?? D.windowDays,
        nouveau: { ...D.nouveau, ...(r.nouveau ?? {}) },
        habitue: { ...D.habitue, ...(r.habitue ?? {}) },
        vip: { ...D.vip, ...(r.vip ?? {}) },
        grosPanier: { ...D.grosPanier, ...(r.grosPanier ?? {}) },
        aRisque: { ...D.aRisque, ...(r.aRisque ?? {}) },
    };
}
